import {ChartModel} from '@xh/hoist/cmp/chart';
import {FormModel} from '@xh/hoist/cmp/form';
import {HoistModel, LoadSpec, PlainObject, TaskObserver, XH, lookup, managed} from '@xh/hoist/core';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {action, observable, observableRef} from '@xh/hoist/mobx';
import {Timer} from '@xh/hoist/utils/async';
import {SECONDS} from '@xh/hoist/utils/datetime';
import {asServerException, getDefaultOptions} from '../../../../../core/ServiceUtils';
import {FullQuote, PriceQuote, StockChartSettings} from '../../../../data/DataTypes';
import {ChartSettingsFormModel} from './ChartSettingsFormModel';
import {getDefaultIndicatorParams, getIndicator} from '../../indicators/TechnicalIndicators';
import {toSeriesType} from '../../impl/OhlcSeriesTypes';
import {ChartsTabModel} from '../../ChartsTabModel';
import {isEmpty} from 'lodash';
import {CHART_DOWN_COLOR, CHART_FLAT_COLOR, CHART_UP_COLOR} from '../../../../cmp/ChartColors';

const VOLUME_UP_COLOR = `${CHART_UP_COLOR}80`,
    VOLUME_DOWN_COLOR = `${CHART_DOWN_COLOR}80`,
    VOLUME_FLAT_COLOR = `${CHART_FLAT_COLOR}99`;

export class StockChartWidgetModel extends HoistModel {
    @managed settingsFormModel: ChartSettingsFormModel = new ChartSettingsFormModel();
    @managed stockChart = new ChartModel({highchartsConfig: this.createChartModel()});
    @managed apiTask = TaskObserver.trackLast();
    @lookup(DashViewModel) viewModel: DashViewModel;
    @lookup(() => ChartsTabModel) chartTabModel: ChartsTabModel;
    @observable accessor warning: string;
    @observableRef accessor quote: FullQuote = null;
    /** Direction of the most recent change vs. the prior tick - null until a second quote arrives. */
    @observable accessor priceTick: 'pos' | 'neg' | null = null;
    @observable accessor bidTick: 'pos' | 'neg' | null = null;
    @observable accessor askTick: 'pos' | 'neg' | null = null;
    isMainChart: boolean;

    /** Annotation currently being edited via the stock-tools popup, or null if it is closed. */
    @observableRef accessor currentAnnotation: PlainObject = null;
    @observable accessor annotationStrokeColor: string = '#2196f3';
    @observable accessor annotationStrokeWidth: number = 2;

    /**
     * Unique per-instance class Highcharts' NavigationBindings uses to find this widget's own
     * stock-tools toolbar - see StockToolsToolbar.ts. Multiple chart widgets can be on-screen at
     * once, and NavigationBindings falls back to a document-wide class lookup, so a class shared
     * across widgets would wire one widget's clicks to another's chart.
     */
    get stockToolsBindingsClassName(): string {
        return `stock-tools-bindings-${this.xhId}`;
    }

    /** Polls the full quote for the header bar - paused (interval -1) whenever there is no ticker. */
    @managed private quoteTimer = Timer.create({
        interval: -1,
        runFn: () => this.loadQuoteAsync()
    });

    /** Last OHLCV rows fetched, retained so settings changes can re-render without a re-fetch. */
    private chartData: number[][] = null;

    get canLink(): boolean {
        return !this.isMainChart;
    }

    get formModel(): FormModel {
        return this.settingsFormModel.formModel;
    }

    /** True if this chart's ticker is driven by the tab, rather than picked here. */
    get followsMainChart(): boolean {
        return this.isMainChart || this.formModel.values.isLinked;
    }

    constructor() {
        super();

        XH.messageHub.subscribe('price_quotes', this.xhId, message => this.updateLastPrice(message.payload));
    }

    override onLinked() {
        const {viewModel, formModel} = this,
            {viewSpec, viewState} = viewModel;

        this.isMainChart = viewSpec.id === 'mainChart';

        // Seed the form from persisted state *before* wiring the reactions below - otherwise this
        // init would round-trip back through them and clobber a pending ticker (see next block).
        this.settingsFormModel.init(viewState as StockChartSettings);

        // The tab owns the main chart's ticker. If it already has one - i.e. we are mounting in
        // response to a "Send to Chart" navigation - it wins over our persisted ticker, for the
        // main chart and for linked charts alike. Otherwise this is a cold start, and the main
        // chart seeds the tab from its own persisted state instead.
        //
        // Note widgets mount in arbitrary order, so a linked chart may get here before the main
        // chart has seeded the tab. That case is covered by the reaction below, which fires once
        // the main chart does seed it.
        const {mainChartTicker} = this.chartTabModel;
        if (mainChartTicker && this.followsMainChart) {
            formModel.setValues({ticker: mainChartTicker});
        } else if (this.isMainChart) {
            this.chartTabModel.setMainChartTicker(formModel.values.ticker);
        }

        this.addReaction(
            {
                // Ticker is the only setting requiring a re-fetch - the rest are chart config.
                track: () => formModel.values.ticker,
                run: ticker => {
                    this.setWarning(ticker ? null : 'Select a ticker');
                    this.setQuote(null);
                    this.quoteTimer.setInterval(ticker ? 3 * SECONDS : -1);
                    if (!ticker) return;

                    if (this.isMainChart) this.chartTabModel.setMainChartTicker(ticker);
                    this.loadAsync();
                    this.loadQuoteAsync();
                }
            },
            {
                track: () => this.chartTabModel.mainChartTicker,
                run: ticker => {
                    if (ticker && this.followsMainChart) formModel.setValues({ticker});
                }
            },
            {
                track: () => formModel.values.isLinked,
                run: isLinked => {
                    const {mainChartTicker} = this.chartTabModel;
                    if (isLinked && mainChartTicker) {
                        formModel.setValues({ticker: mainChartTicker});
                    }
                }
            },
            {
                track: () => this.settingsFormModel.getSettings(),
                run: settings => {
                    this.saveSettings(settings);
                    this.updateChartSettings(settings);
                }
            }
        );

        // Initial paint of chart config + view title. The data itself is loaded by the framework,
        // which calls `loadAsync()` on this (linked) model once mounted.
        const settings = this.settingsFormModel.getSettings();
        this.setWarning(settings.ticker ? null : 'Select a ticker');
        this.saveSettings(settings);
        this.updateChartSettings(settings);

        this.quoteTimer.setInterval(settings.ticker ? 3 * SECONDS : -1);
        this.loadQuoteAsync();
    }

    @action
    private setWarning(warning: string) {
        this.warning = warning;
    }

    /** Opens the stroke color/width popup for an annotation clicked on the chart. */
    @action
    openAnnotationEditor(annotation: PlainObject) {
        const {typeOptions} = annotation.options,
            shape = annotation.options.shapes?.[0];

        this.currentAnnotation = annotation;
        this.annotationStrokeColor = typeOptions?.line?.stroke ?? shape?.stroke ?? '#2196f3';
        this.annotationStrokeWidth = typeOptions?.line?.strokeWidth ?? shape?.['stroke-width'] ?? 2;
    }

    @action
    closeAnnotationEditor() {
        this.currentAnnotation = null;
    }

    @action
    setAnnotationStrokeColor(color: string) {
        this.annotationStrokeColor = color;
    }

    @action
    setAnnotationStrokeWidth(width: number) {
        this.annotationStrokeWidth = width;
    }

    /** Applies the popup's color/width to the annotation being edited, then closes the popup. */
    applyAnnotationStyle() {
        const {currentAnnotation: annotation, annotationStrokeColor: stroke, annotationStrokeWidth: strokeWidth} = this;
        if (!annotation) return;

        // Stock/advanced annotations (fibonacci, pitchfork, etc.) carry their styling under
        // typeOptions - basic shapes/labels take `shapes`/`labels` directly.
        if (annotation.options.typeOptions) {
            annotation.update({
                typeOptions: {
                    lineColor: stroke,
                    lineWidth: strokeWidth,
                    line: {strokeWidth, stroke},
                    background: {strokeWidth, stroke},
                    innerBackground: {strokeWidth, stroke},
                    outerBackground: {strokeWidth, stroke},
                    connector: {strokeWidth, stroke}
                }
            });
        } else {
            annotation.update({
                shapes: [{'stroke-width': strokeWidth, stroke}],
                labels: [{borderWidth: strokeWidth, borderColor: stroke}]
            });
        }
        this.closeAnnotationEditor();
    }

    private updateLastPrice(quote: PriceQuote) {
        if (quote.ticker != this.settingsFormModel.getSettings().ticker) return;

        // Header bar's price/change is left to the quoteTimer poll below (via loadQuoteAsync) -
        // this feed's own priceChange/priceChangePct are momentarily 0 on some ticks, which made
        // the header flicker when it was wired up to track them directly.
        if (this.stockChart.series.length < 1) return;

        // Look up the price series by id, rather than assuming it is always `series[0]` - settings
        // changes tear down and reinsert every series (indicators force a full destroy/reinit even
        // when unrelated settings change), and a quote arriving mid-rebuild could otherwise land on
        // the wrong series (e.g. volume) and corrupt its last point.
        const series = this.stockChart.highchart?.get('main') as PlainObject,
            lastPoint = series?.points?.[series.points.length - 1];

        if (!lastPoint) return;

        // Every plotted type now carries a full OHLC bar (see OhlcSeriesTypes), so the last close
        // can be updated the same way regardless of how the series is rendered.
        lastPoint.update({close: quote.price});
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const settings = this.settingsFormModel.getSettings();
        if (settings.ticker) {
            await this.updateChartData(settings);
        }
    }

    @action
    private setQuote(quote: FullQuote) {
        const prev = this.quote;
        if (!quote) {
            this.priceTick = this.bidTick = this.askTick = null;
        } else if (prev) {
            const prevPrice = (prev.bid + prev.ask) / 2,
                price = (quote.bid + quote.ask) / 2;
            this.priceTick = this.tickFor(price, prevPrice, this.priceTick);
            this.bidTick = this.tickFor(quote.bid, prev.bid, this.bidTick);
            this.askTick = this.tickFor(quote.ask, prev.ask, this.askTick);
        }
        this.quote = quote;
    }

    /** Direction vs. the prior value - an unchanged value holds its last known direction. */
    private tickFor(value: number, prevValue: number, lastTick: 'pos' | 'neg' | null): 'pos' | 'neg' | null {
        if (value > prevValue) return 'pos';
        if (value < prevValue) return 'neg';
        return lastTick;
    }

    /**
     * Resolves the app's default text color for use as a Highcharts stroke/border color - see
     * `getTickColors()` below for why a probe element is needed rather than reading the
     * `--xh-text-color` custom property directly. Used as the default color for freshly-drawn
     * stock-tools annotations, since Highcharts' own default is a near-black that reads fine on
     * the light theme but is illegible on dark.
     */
    private getAnnotationColor(): string {
        const probe = document.createElement('span');
        probe.style.display = 'none';
        document.body.appendChild(probe);
        const color = getComputedStyle(probe).color;
        document.body.removeChild(probe);
        return color;
    }

    /** Cache of pos/neg colors, keyed by theme - avoided the memoization per its cost per render event. */
    private tickColorCache: {darkTheme: boolean; pos: string; neg: string} = null;

    /**
     * Resolves the same `xh-pos-val`/`xh-neg-val` colors used elsewhere in the app for use as an
     * SVG fill - reading the custom property directly (e.g. `--xh-pos-val-color`) returns its
     * unresolved `var(...)` text rather than a usable color, so a probe element is used instead to
     * pick up the fully-resolved `color` the class applies.
     */
    private getTickColors(): {pos: string; neg: string} {
        const {darkTheme} = XH;
        if (!this.tickColorCache || this.tickColorCache.darkTheme !== darkTheme) {
            const probe = document.createElement('span');
            probe.style.display = 'none';
            document.body.appendChild(probe);

            probe.className = 'xh-pos-val';
            const pos = getComputedStyle(probe).color;
            probe.className = 'xh-neg-val';
            const neg = getComputedStyle(probe).color;

            document.body.removeChild(probe);
            this.tickColorCache = {darkTheme, pos, neg};
        }
        return this.tickColorCache;
    }

    /** Populates the header bar with name/price/change/bid/ask for the current ticker. */
    private async loadQuoteAsync() {
        const {ticker} = this.settingsFormModel.getSettings();
        if (!ticker) return;

        try {
            const quote: FullQuote = await XH.fetchService.getJson({
                url: `${XH.getConf('svcApiUrl')}/quote/fullquote/${ticker}`,
                ...getDefaultOptions()
            });

            // Ticker may have changed while this fetch was in flight.
            if (ticker !== this.settingsFormModel.getSettings().ticker) return;
            this.setQuote(quote);
        } catch (e) {
            console.error(`Failed to load quote for ${ticker}`, e);
        }
    }

    @action
    private saveSettings(settings: StockChartSettings) {
        this.viewModel.viewState = settings;
        this.viewModel.title = `${settings.ticker} - ${settings.range}`;
    }

    private updateChartSettings(settings: StockChartSettings) {
        this.stockChart.updateHighchartsConfig({
            navigator: {enabled: settings.showNavigator},
            xAxis: {ordinal: settings.ordinal, range: this.rangeToTime(settings.range)},
            yAxis: this.getPanelSizes(settings)
        });

        // Rebuild from the data we already hold - chart type, indicator and indicator params are
        // all series-level config, and none of them require another trip to the server.
        this.renderSeries(settings);
    }

    private getPanelSizes(settings: StockChartSettings) {
        const sizes = [];
        const indicator = getIndicator(settings.indicator);

        if (!indicator || indicator.panel === 'overlay') {
            if (settings.showVolume) {
                sizes.push({height: '80%', ...this.getMainAxis()});
                sizes.push({height: '0%', ...this.getOtherAxis()});
                sizes.push({height: '18%', top: '82%', ...this.getOtherAxis()});
            } else {
                sizes.push({height: '100%', ...this.getMainAxis()});
                sizes.push({height: '0%', ...this.getOtherAxis()});
                sizes.push({height: '0%', ...this.getOtherAxis()});
            }
        } else {
            if (settings.showVolume) {
                sizes.push({height: '60%', ...this.getMainAxis()});
                sizes.push({height: '18%', top: '62%', ...this.getOtherAxis()});
                sizes.push({height: '18%', top: '82%', ...this.getOtherAxis()});
            } else {
                sizes.push({height: '80%', ...this.getMainAxis()});
                sizes.push({height: '18%', top: '82%', ...this.getOtherAxis()});
                sizes.push({height: '0%', ...this.getOtherAxis()});
            }
        }

        return sizes;
    }

    private getMainAxis(): PlainObject {
        return {title: {text: null}, crosshair: {dashStyle: 'ShortDot'}, opposite: true, gridLineDashStyle: 'Dot'};
    }

    private getOtherAxis(): PlainObject {
        return {title: {text: null}, opposite: true, offset: 0};
    }

    private async updateChartData(settings: StockChartSettings) {
        try {
            this.chartData = await XH.chartService.fetchTimeSeriesAsync(settings.ticker).linkTo(this.apiTask);

            this.renderSeries(settings);
        } catch (e) {
            this.chartData = null;
            XH.handleException(asServerException(e), {
                title: 'Unable to get data for ' + settings.ticker
            });
        }
    }

    /**
     * Build the chart's series from the data already held, in the fixed order the axis layout in
     * `getPanelSizes()` assumes: price, overlay, oscillator, volume.
     */
    private renderSeries(settings: StockChartSettings) {
        const {chartData} = this;
        if (!chartData) return;

        if (isEmpty(chartData)) {
            this.stockChart.clear();
            this.setWarning('No data');
            return;
        }

        this.setWarning(null);

        const indicator = getIndicator(settings.indicator),
            // Volume is mandatory for several indicators, so the series is always present and is
            // hidden rather than omitted when the user turns it off.
            indicatorParams = {
                ...getDefaultIndicatorParams(settings.indicator),
                ...(settings.indicatorParams?.[settings.indicator] ?? {}),
                volumeSeriesID: 'volume'
            };

        const priceSeries = {
            name: settings.ticker,
            id: 'main',
            type: toSeriesType(settings.type),
            yAxis: 0,
            // Cloned - Highcharts may mutate a series' `data` option in place (its own update()
            // docs warn of this), and `updateLastPrice()` patches the last point on every tick.
            // Handing it the live `chartData` reference let those mutations corrupt our cache,
            // which volume's own series (derived from `chartData` on every re-render) would then
            // silently inherit.
            data: chartData.map(d => d.slice())
        };

        const volSeries = {
            id: 'volume',
            name: 'volume',
            type: 'column',
            yAxis: 2,
            borderWidth: 0,
            // Each bar takes its candle's direction, faded so it sits behind the price. Bars merged by
            // data grouping lose their point color and fall back to the neutral series color.
            color: VOLUME_FLAT_COLOR,
            turboThreshold: 0,
            data: chartData.map(d => ({x: d[0], y: d[5], color: d[4] < d[1] ? VOLUME_DOWN_COLOR : VOLUME_UP_COLOR}))
            // Kept Highcharts-visible at all times - showVolume is expressed purely via the
            // yAxis height/top in getPanelSizes(). Toggling Highcharts' own `visible` flag here
            // used to drop this series from axis extent calc (chart.ignoreHiddenSeries defaults
            // to true), so re-showing it later left the axis with no min/max and no plotted points.
        };

        const overlaySeries = {
            id: 'overlay',
            type: indicator?.panel === 'overlay' ? settings.indicator : null,
            params: indicatorParams,
            linkedTo: 'main',
            yAxis: 0
        };

        const oscillatorSeries = {
            id: 'oscillator',
            type: indicator?.panel === 'oscillator' ? settings.indicator : null,
            params: indicatorParams,
            linkedTo: 'main',
            yAxis: 1
        };

        this.stockChart.setSeries([priceSeries, overlaySeries, oscillatorSeries, volSeries]);
    }

    private createChartModel() {
        const model = this;
        return {
            navigator: {enabled: true},
            rangeSelector: {enabled: false, selected: 1},
            exporting: {enabled: false},
            legend: {enabled: false},
            // scrollbar: {enabled: false},
            xAxis: {type: 'datetime', ordinal: true, tickPosition: 'inside', crosshair: true},
            yAxis: [
                {
                    title: {text: 'price'},
                    //opposite: true,
                    height: '60%',
                    crosshair: true
                },
                {
                    title: {text: 'something'},
                    //opposite: true,
                    height: '18%',
                    top: '62%',
                    offset: 0
                },
                {
                    title: {text: 'volume'},
                    //opposite: true,
                    height: '18%',
                    top: '82%',
                    offset: 0
                }
            ],

            plotOptions: {
                series: {
                    animation: false,
                    marker: {enabled: false},
                    states: {
                        hover: {enabled: false}
                    }
                },
                // Solid green/red bars with matching wicks, as on a trading screen.
                candlestick: {
                    color: CHART_DOWN_COLOR,
                    lineColor: CHART_DOWN_COLOR,
                    upColor: CHART_UP_COLOR,
                    upLineColor: CHART_UP_COLOR
                },
                hollowcandlestick: {
                    color: CHART_DOWN_COLOR,
                    upColor: CHART_UP_COLOR
                },
                ohlc: {
                    color: CHART_DOWN_COLOR,
                    upColor: CHART_UP_COLOR
                },
                hlc: {
                    color: CHART_DOWN_COLOR,
                    upColor: CHART_UP_COLOR
                }
            },

            // Drawing tools - see StockToolsToolbar.ts for the toolbar buttons these bindings
            // drive, modeled on https://www.highcharts.com/demo/stock/stock-tools-custom-gui
            navigation: {
                bindingsClassName: this.stockToolsBindingsClassName,
                // Highcharts' own defaults are a near-black stroke, illegible on a dark theme -
                // merged into every annotation the stock-tools bindings create (see e.g.
                // StockToolsBindings.js `segment.start()`), so this only sets the *initial* color -
                // applyAnnotationStyle()'s own `annotation.update()` calls still win after that.
                //
                // `typeOptions`/`labelOptions` are safe to set for every binding - annotation
                // types that don't use them (e.g. crookedLine's typeOptions.line vs. basicAnnotation's
                // shapes/labels) simply ignore the unused key. `shapes`, however, is NOT safe to set
                // here: basicAnnotation bindings with no shapes of their own (labelAnnotation) would
                // otherwise get a phantom shapes[0] with no `type`, which crashes Highcharts' shape
                // renderer - so shapes stroke is set per-binding below, only for the bindings that
                // already define their own shapes array.
                annotationsOptions: {
                    typeOptions: {line: {stroke: this.getAnnotationColor()}},
                    labelOptions: {borderColor: this.getAnnotationColor()}
                },
                bindings: {
                    circleAnnotation: {annotationsOptions: {shapes: [{stroke: this.getAnnotationColor()}]}},
                    ellipseAnnotation: {annotationsOptions: {shapes: [{stroke: this.getAnnotationColor()}]}},
                    rectangleAnnotation: {annotationsOptions: {shapes: [{stroke: this.getAnnotationColor()}]}}
                },
                events: {
                    selectButton(event: PlainObject) {
                        this.chart.activeButton = event.button;
                    },
                    deselectButton() {
                        this.chart.activeButton = null;
                    },
                    showPopup(event: PlainObject) {
                        // Fired when an existing annotation is clicked - skip while a new one is
                        // still being placed (`activeButton` set).
                        if (event.formType === 'annotation-toolbar' && !this.chart.activeButton) {
                            model.openAnnotationEditor(event.annotation);
                        }
                    },
                    closePopup() {
                        model.closeAnnotationEditor();
                    }
                }
            },
            stockTools: {gui: {enabled: false}},

            lang: {noData: 'Select a ticker '},
            noData: {
                style: {
                    fontWeight: 'bold',
                    fontSize: '30px'
                }
            },

            tooltip: {
                split: true,

                valueDecimals: 2,
                xDateFormat: '%Y-%m-%d'
            },

            chart: {
                events: {
                    render() {
                        try {
                            let chart = this;

                            if (chart.series.length < 1) return;

                            let points = chart.series[0].points,
                                lastPoint = points[points.length - 1];

                            if (!lastPoint) return;

                            // Same pos/neg direction logic used for the header's price/bid/ask -
                            // compared against the value last shown here (not the prior bar's close,
                            // which barely moves while ticks stream into the still-open last bar) -
                            // and retains its last known direction when the value is unchanged.
                            chart.lastLabelTick = model.tickFor(lastPoint.y, chart.lastLabelValue, chart.lastLabelTick);
                            chart.lastLabelValue = lastPoint.y;

                            let x = lastPoint.plotX + chart.plotLeft,
                                y = lastPoint.plotY + chart.plotTop;

                            if (chart.lastLabel) {
                                chart.lastLabel.destroy();
                            }
                            const lbl = (Math.round(lastPoint.y * 100) / 100).toFixed(2),
                                tickColors = model.getTickColors(),
                                fill = chart.lastLabelTick === 'pos' ? tickColors.pos : chart.lastLabelTick === 'neg' ? tickColors.neg : chart.series[0].color;
                            chart.lastLabel = chart.renderer
                                .label(lbl, x + 6, y - 12, 'callout', x, y)
                                .css({
                                    color: '#FFFFFF',
                                    fontSize: '0.8em'
                                })
                                .attr({
                                    fill,
                                    padding: 3,
                                    r: 5,
                                    zIndex: 60
                                })
                                .add();
                        } catch (e) {
                            console.error(e);
                        }
                    }
                }
            }
        };
    }

    private rangeToTime(range: string): number {
        const date = new Date();
        let days = 5000; // ALL
        switch (range) {
            case '1M':
                days = 30;
                break;
            case '3M':
                days = 60;
                break;
            case '6M':
                days = 180;
                break;
            case 'YTD':
                days = (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(date.getFullYear(), 0, 0)) / 24 / 60 / 60 / 1000;
                break;
        }

        return days * 24 * 3600 * 1000;
    }
}
